import type { AcaoDetalhada, BloqueioDeGrupo, GrupoDeCampo } from '@/types/api';

/**
 * Grupos de campo da ação. Especificação V3.1, item 2.1.
 *
 * QUEM DECIDE SE UM GRUPO ESTÁ ABERTO É O SERVIDOR, em `gruposEditaveis`. Este arquivo
 * só traduz o que ele respondeu: rótulo, descrição e a leitura do motivo do bloqueio.
 * A matriz de 10 situações por 4 grupos, e o cruzamento dela com as permissões do
 * usuário, ficam no domínio — reimplementar aqui criaria duas versões da mesma regra.
 */

export const rotuloDoGrupo: Record<GrupoDeCampo, string> = {
  Origem: 'Origem',
  Direcionamento: 'Direcionamento',
  Processo: 'Processo',
  Operacional: 'Acompanhamento',
  Task: 'Task no Azure DevOps',
};

/**
 * O que cada grupo reúne, em uma linha.
 *
 * Existe porque "Origem" e "Processo" não significam nada para quem abre a tela pela
 * primeira vez. Dizer o que está dentro custa uma linha e evita a pergunta.
 */
export const descricaoDoGrupo: Record<GrupoDeCampo, string> = {
  Origem: 'O que o CS escreveu: assunto, descrição, plano de ação e clientes afetados.',
  Direcionamento: 'A área responsável pela ação.',
  Processo: 'O que o Produto controla: responsável, prioridade e prazo.',
  Operacional: 'Comentários, impedimentos e referências externas.',
  Task: 'A task vinculada no Azure DevOps.',
};

/** Estado de um grupo para o usuário que está na tela. */
export interface EstadoDoGrupo {
  editavel: boolean;
  /** Presente quando o grupo está fechado. Texto pronto, vindo do servidor. */
  bloqueio: BloqueioDeGrupo | undefined;
}

/**
 * Lê o estado de um grupo na resposta da ação.
 *
 * Fechado sem motivo declarado não deveria acontecer: o servidor envia um bloqueio para
 * cada grupo fora de `gruposEditaveis`. Se acontecer, `bloqueio` fica `undefined` e a tela
 * mostra o grupo como leitura sem explicação — pior do que explicar, melhor do que abrir
 * um campo que vai ser recusado.
 */
export function estadoDoGrupo(acao: AcaoDetalhada, grupo: GrupoDeCampo): EstadoDoGrupo {
  const editavel = acao.gruposEditaveis.includes(grupo);

  return {
    editavel,
    bloqueio: editavel ? undefined : acao.bloqueios.find((b) => b.grupo === grupo),
  };
}
